import React, { useState, useEffect, useMemo, useRef } from "react";
import { Search, X, Save, ChevronLeft, ImageIcon, Video, Edit3 } from "lucide-react";
import { db, storage, auth } from "../firebase";
import {
  collection, onSnapshot, query, orderBy,
  doc, updateDoc, serverTimestamp, deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const cx = (...list) => list.filter(Boolean).join(" ");

function AInput({ value, onChange, placeholder, type = "text", disabled }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} disabled={disabled}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        disabled:bg-slate-50 disabled:text-slate-400 placeholder:text-slate-400 transition" />
  );
}

function ATextarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        placeholder:text-slate-400 transition resize-none" />
  );
}

function Field({ label, children }) {
  return (
    <div>
      {label && <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>}
      {children}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="flex items-center gap-2 pt-2 pb-1">
      <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">{children}</span>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
  );
}

/* ── Mapper'lar ── */
function photoToForm(id, d) {
  return {
    _fbId: id,
    title: d.title || "",
    description: d.description || "",
    imageUrl: d.imageUrl || "",
    location: d.location || "",
    imageFile: null,
    imagePreview: d.imageUrl || "",
  };
}

function videoToForm(id, d) {
  return {
    _fbId: id,
    title: d.title || "",
    description: d.description || "",
    videoUrl: d.videoUrl || "",
    thumbnailUrl: d.thumbnailUrl || "",
    videoFile: null,
  };
}

export default function PhotoVideoEditTab({ loading, setLoading, showStatus }) {
  const [mode, setMode] = useState("photo"); // photo | video

  /* ── Veriler ── */
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "resimler"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setPhotos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setDataLoading(false);
    }, () => setDataLoading(false));
    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "videolar"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setVideos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  /* ── Arama & seçim ── */
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const list = mode === "photo" ? photos : videos;

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return list;
    return list.filter((item) =>
      `${item.title || ""} ${item.description || ""} ${item.location || ""}`
        .toLocaleLowerCase("tr").includes(q)
    );
  }, [list, search]);

  const set = (patch) => setSelected((p) => ({ ...p, ...patch }));

  /* ── Dosya ref'leri ── */
  const imageFileRef = useRef(null);
  const videoFileRef = useRef(null);

  const handleSelect = (item) => {
    if (mode === "photo") setSelected(photoToForm(item.id, item));
    else setSelected(videoToForm(item.id, item));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm(`"${item.title || "Bu kayıt"}" silinsin mi?`)) return;
    try {
      const col = mode === "photo" ? "resimler" : "videolar";
      await deleteDoc(doc(db, col, item.id));
      showStatus("success", "Kayıt silindi.");
    } catch (err) {
      showStatus("error", "Silinemedi: " + err.message);
    }
  };

  /* ── Kaydet ── */
  const handleSave = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setLoading(true);
    try {
      const user = auth.currentUser;
      const { _fbId, imageFile, videoFile, imagePreview, ...rest } = selected;

      let payload = { ...rest, updatedAt: serverTimestamp() };

      if (mode === "photo" && imageFile) {
        const fname = `photo_${Date.now()}_${imageFile.name}`;
        const sRef = ref(storage, `resimler/${user?.uid}/${fname}`);
        await uploadBytes(sRef, imageFile);
        payload.imageUrl = await getDownloadURL(sRef);
      }

      if (mode === "video" && videoFile) {
        const fname = `video_${Date.now()}_${videoFile.name}`;
        const sRef = ref(storage, `videolar/${user?.uid}/${fname}`);
        await uploadBytes(sRef, videoFile);
        payload.videoUrl = await getDownloadURL(sRef);
      }

      const col = mode === "photo" ? "resimler" : "videolar";
      await updateDoc(doc(db, col, _fbId), payload);
      showStatus("success", "Güncellendi.");
      setSelected(null);
    } catch (err) {
      showStatus("error", "Hata: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ════ RENDER ════ */

  /* ── Düzenleme formu ── */
  if (selected) return (
    <div>
      <button type="button" onClick={() => setSelected(null)}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-5 transition">
        <ChevronLeft className="w-4 h-4" /> Listeye Dön
      </button>

      <div className="flex items-center gap-2 mb-4">
        <Edit3 className="w-4 h-4 text-amber-500" />
        <h3 className="font-semibold text-slate-900 text-sm">
          {mode === "photo" ? "Resmi Düzenle" : "Videoyu Düzenle"}
        </h3>
      </div>

      <form onSubmit={handleSave} className="space-y-5">

        {mode === "photo" ? (
          <>
            <SectionHeading>Temel Bilgiler</SectionHeading>
            <div className="space-y-3">
              <Field label="Başlık">
                <AInput value={selected.title} onChange={(e) => set({ title: e.target.value })} placeholder="Başlık…" />
              </Field>
              <Field label="Açıklama">
                <ATextarea value={selected.description} onChange={(e) => set({ description: e.target.value })} rows={3} placeholder="Açıklama…" />
              </Field>
              <Field label="Konum / Köy / İlçe">
                <AInput value={selected.location} onChange={(e) => set({ location: e.target.value })} placeholder="Zara / Ekinli Köyü" />
              </Field>
            </div>

            <SectionHeading>Görsel</SectionHeading>

            {/* Önizleme */}
            {selected.imagePreview && (
              <div className="w-full max-h-48 rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                <img src={selected.imagePreview} alt="önizleme"
                  className="w-full h-full object-contain max-h-48" />
              </div>
            )}

            <Field label="Görsel URL">
              <AInput
                value={selected.imageFile ? "" : selected.imageUrl}
                onChange={(e) => {
                  set({ imageUrl: e.target.value, imagePreview: e.target.value, imageFile: null });
                  if (imageFileRef.current) imageFileRef.current.value = "";
                }}
                placeholder="https://…"
                disabled={!!selected.imageFile}
              />
            </Field>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-100" />
              <span className="text-xs text-slate-400">veya dosya yükle</span>
              <div className="flex-1 h-px bg-slate-100" />
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer text-sm text-slate-700 transition">
                <ImageIcon className="w-4 h-4 text-slate-400" />
                {selected.imageFile ? selected.imageFile.name : "Dosya Seç"}
                <input ref={imageFileRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    set({ imageFile: f, imagePreview: URL.createObjectURL(f), imageUrl: "" });
                  }} />
              </label>
              {selected.imageFile && (
                <button type="button"
                  onClick={() => {
                    set({ imageFile: null, imagePreview: selected.imageUrl });
                    if (imageFileRef.current) imageFileRef.current.value = "";
                  }}
                  className="text-xs text-red-500 hover:text-red-700 transition">
                  Kaldır
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <SectionHeading>Temel Bilgiler</SectionHeading>
            <div className="space-y-3">
              <Field label="Başlık">
                <AInput value={selected.title} onChange={(e) => set({ title: e.target.value })} placeholder="Başlık…" />
              </Field>
              <Field label="Açıklama">
                <ATextarea value={selected.description} onChange={(e) => set({ description: e.target.value })} rows={3} placeholder="Açıklama…" />
              </Field>
            </div>

            <SectionHeading>Video</SectionHeading>

            <Field label="Video URL (YouTube / Vimeo)">
              <AInput
                value={selected.videoFile ? "" : selected.videoUrl}
                onChange={(e) => {
                  set({ videoUrl: e.target.value, videoFile: null });
                  if (videoFileRef.current) videoFileRef.current.value = "";
                }}
                placeholder="https://youtube.com/…"
                disabled={!!selected.videoFile}
              />
            </Field>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-100" />
              <span className="text-xs text-slate-400">veya dosya yükle</span>
              <div className="flex-1 h-px bg-slate-100" />
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer text-sm text-slate-700 transition">
                <Video className="w-4 h-4 text-slate-400" />
                {selected.videoFile ? selected.videoFile.name : "Video Dosyası Seç"}
                <input ref={videoFileRef} type="file" accept="video/*" className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    set({ videoFile: f, videoUrl: "" });
                  }} />
              </label>
              {selected.videoFile && (
                <button type="button"
                  onClick={() => {
                    set({ videoFile: null });
                    if (videoFileRef.current) videoFileRef.current.value = "";
                  }}
                  className="text-xs text-red-500 hover:text-red-700 transition">
                  Kaldır
                </button>
              )}
            </div>

            <SectionHeading>Thumbnail</SectionHeading>
            <Field label="Thumbnail URL (opsiyonel)">
              <AInput value={selected.thumbnailUrl} onChange={(e) => set({ thumbnailUrl: e.target.value })} placeholder="https://…" />
            </Field>

            {/* Thumbnail önizleme */}
            {selected.thumbnailUrl && (
              <div className="w-32 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                <img src={selected.thumbnailUrl} alt="thumbnail"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = "none"; }} />
              </div>
            )}
          </>
        )}

        <div className="pt-2 flex gap-3">
          <button type="submit" disabled={loading}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50">
            <Save className="w-4 h-4" />
            {loading ? "Kaydediliyor…" : "Güncelle"}
          </button>
          <button type="button" onClick={() => setSelected(null)}
            className="px-4 py-2.5 rounded-xl text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition">
            İptal
          </button>
        </div>
      </form>
    </div>
  );

  /* ── Liste görünümü ── */
  return (
    <div>
      {/* Tür toggle */}
      <div className="flex gap-2 mb-4">
        {[
          { v: "photo", label: "Resimler", icon: ImageIcon },
          { v: "video", label: "Videolar", icon: Video },
        ].map(({ v, label, icon: Icon }) => (
          <button key={v} type="button"
            onClick={() => { setMode(v); setSearch(""); setSelected(null); }}
            className={cx(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition",
              mode === v
                ? "bg-slate-900 text-white border-slate-900"
                : "border-slate-200 text-slate-600 hover:bg-slate-50"
            )}>
            <Icon className="w-3.5 h-3.5" />
            {label}
            <span className={cx(
              "text-[10px] px-1.5 py-0.5 rounded-full font-medium",
              mode === v ? "bg-white text-slate-900" : "bg-slate-100 text-slate-500"
            )}>
              {v === "photo" ? photos.length : videos.length}
            </span>
          </button>
        ))}
      </div>

      {/* Arama */}
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Başlık, açıklama veya konum ara…"
          className="w-full pl-9 pr-9 py-2.5 text-sm border border-slate-200 rounded-xl
            focus:outline-none focus:ring-2 focus:ring-slate-900 bg-slate-50 placeholder:text-slate-400" />
        {search && (
          <button onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <p className="text-xs text-slate-400 mb-3">
        <span className="font-medium text-slate-600">{filtered.length}</span> kayıt listeleniyor
      </p>

      {dataLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {!dataLoading && (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
          {filtered.map((item) => (
            <button key={item.id} type="button" onClick={() => handleSelect(item)}
              className="w-full flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200
                hover:border-slate-300 hover:shadow-sm transition text-left group">

              {/* Önizleme */}
              <div className="w-10 h-10 rounded-lg border border-slate-100 bg-slate-50 overflow-hidden shrink-0 flex items-center justify-center">
                {mode === "photo" && item.imageUrl
                  ? <img src={item.imageUrl} alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = "none"; }} />
                  : mode === "video" && item.thumbnailUrl
                  ? <img src={item.thumbnailUrl} alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = "none"; }} />
                  : mode === "photo"
                  ? <ImageIcon className="w-4 h-4 text-slate-300" />
                  : <Video className="w-4 h-4 text-slate-300" />
                }
              </div>

              {/* Bilgi */}
              <div className="flex-1 min-w-0">
                <span className="font-medium text-sm text-slate-900 truncate block">
                  {item.title || "—"}
                </span>
                <span className="text-xs text-slate-400 truncate block">
                  {mode === "photo"
                    ? (item.location || item.description || "—")
                    : (item.videoUrl || item.description || "—")
                  }
                </span>
              </div>

              {/* Sil */}
              <button type="button" onClick={(e) => handleDelete(e, item)}
                className="shrink-0 p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition"
                title="Sil">
                <X className="w-4 h-4" />
              </button>

              <Edit3 className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 shrink-0 transition" />
            </button>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <p className="text-sm">Kayıt bulunamadı.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}